import { Box, Button, Typography, useTheme } from '@mui/material';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import Mui from 'materials';
import React, { useMemo, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';

import Divider from 'components/divider';
import InputNumber from 'components/InputNumber';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import NumberField from 'components/number-field';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';

import { Aerator } from 'views/production-systems/features/farm-setup/types/aerators.type';
import { UpdateAeratorInputForm } from 'views/production-systems/features/farm-setup/types/update-aerators-input.type';
import { AeratorTypes } from 'views/self-service/features/aerators/types/aerator-type.enum';

import { isValidPositive } from 'utils/validate-numbers';
import { useAeratorsState } from 'views/self-service/features/aerators/context/aerators-context';

type Props = {
  fieldIndex: number;
  pondCurrentBiomass: number;
};

const PAGE_NAME_ID = 'productionSystems.farmSetup.dialogs';

const ListAeratorsContainer: React.FC<Props> = ({ fieldIndex, pondCurrentBiomass }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { aerators } = useAeratorsState();
  const [searchValue, setSearchVale] = useState<string>('');
  const [openForm, setOpenForm] = useState<boolean>(false);

  // #region FORMS
  const {
    reset,
    handleSubmit,
    formState: { isValid },
    control: aeratorFormControl,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      id: 0,
      quantity: 1,
    },
  });

  const {
    watch,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<UpdateAeratorInputForm>();

  const {
    update,
    prepend,
    fields: rows,
  } = useFieldArray({
    control: control,
    name: `ponds.${fieldIndex}.assignedAerators`,
    keyName: '_id',
  });

  const watchAerators = watch(`ponds.${fieldIndex}.assignedAerators`);

  // #endregion

  // #region HANDLERS
  const handleAddAerator = (formData: { id: number; quantity: number }) => {
    const { id: aeratorId, quantity: aeratorQuantity } = formData;

    const allAerators = getValues(`ponds.${fieldIndex}.assignedAerators`);
    const existingAeratorIndex = allAerators.findIndex((aer) => aer.aeratorId === aeratorId);

    if (existingAeratorIndex !== -1) {
      const aeratorToUpdate = allAerators[existingAeratorIndex];
      update(existingAeratorIndex, {
        ...aeratorToUpdate,
        numberOfAerators: aeratorQuantity,
        isDeleted: false,
        edit: false,
      });

      reset();
      return;
    }

    const aeratorToAdd = aerators?.find((aer) => aer.id == aeratorId) as Aerator;

    if (!aeratorToAdd) return;

    prepend({
      aeratorId: aeratorToAdd.id,
      numberOfAerators: aeratorQuantity,
      aeratorBrand: aeratorToAdd.brand,
      aeratorSubTypeId: aeratorToAdd.subType,
      horsepower: aeratorToAdd.horsepower,
      supportedBiomass: aeratorToAdd.biomassSupported,
      aeratorName: aeratorToAdd.name,
      aeratorTypeId: aeratorToAdd.type,
      aeratorRegDate: '',
      edit: false,
      isDeleted: false,
      isNew: true,
    });

    reset();
  };

  const handleDeleteAerator = (rowIndex: number) => {
    const aerator = getValues(`ponds.${fieldIndex}.assignedAerators.${rowIndex}`);
    setValue(`ponds.${fieldIndex}.assignedAerators.${rowIndex}.numberOfAerators`, 0);
    update(rowIndex, {
      ...aerator,
      isDeleted: true,
    });
  };

  const handleEditRow = (rowIndex: number) => {
    let aerator = getValues(`ponds.${fieldIndex}.assignedAerators.${rowIndex}`);
    aerator.edit = !aerator.edit;
    setValue(`ponds.${fieldIndex}.assignedAerators.${rowIndex}`, aerator);
  };

  const handleConfirm = (rowIndex: number) => {
    let aerator = getValues(`ponds.${fieldIndex}.assignedAerators.${rowIndex}`);
    aerator.edit = !aerator.edit;
    setValue(`ponds.${fieldIndex}.assignedAerators.${rowIndex}`, aerator);
  };
  // #endregion

  const filteredAerators = useMemo(() => {
    return aerators
      ?.filter((aerator) => !aerator.hidden)
      .map((aerator) => {
        const isAdded = rows.some((row) => Number(row.aeratorId) === aerator.id && !row.isDeleted);
        return {
          ...aerator,
          isDisabled: isAdded,
          name: isAdded ? `${aerator.name} ${intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.added` })}` : aerator.name,
        };
      });
  }, [aerators, rows]);

  const options: HeadCell[] = [
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.name` }),
      id: 'name',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.brand` }),
      id: 'brand',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.noUnits` }),
      id: 'units',
      align: 'center',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.type` }),
      id: 'type',
      align: 'right',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.horsePower` }),
      id: 'horsePower',
      align: 'right',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.supportedBiomass` }),
      id: 'biomassSupported',
      align: 'right',
    },
    {
      label: '',
      id: 'actions',
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box gap={4} display={'flex'} flexDirection={openForm ? 'column' : 'row'} justifyContent={openForm ? 'center' : 'flex-end'}>
        <Box
          gap={4}
          width={'100%'}
          display={'flex'}
          justifyContent={'space-between'}
          flexDirection={openForm ? 'column' : 'row'}
          alignItems={!openForm ? 'center' : 'auto'}>
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection="row" justifyContent={'space-between'}>
              <Typography variant="h6" noWrap>
                <FormattedMessage id={`${PAGE_NAME_ID}.aerators.currentCapacity`} values={{ biomass: pondCurrentBiomass || 0 }} />
              </Typography>
            </Box>
          </Box>
          {!openForm ? (
            <Button hidden={openForm} onClick={() => setOpenForm(!openForm)}>
              <FormattedMessage id={`${PAGE_NAME_ID}.aerators.addAerators`} />
            </Button>
          ) : (
            <>
              <Box display={'flex'} flexDirection={'row'} justifyContent={'space-evenly'} gap={4}>
                <Select
                  dark
                  nested
                  fullWidth
                  controller={{
                    name: 'id',
                    control: aeratorFormControl,
                    rules: {
                      validate: {
                        required: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.required' })),
                      },
                    },
                  }}
                  stopPropagation
                  search={{
                    value: searchValue,
                    onChange: setSearchVale,
                  }}
                  onOpen={() => {
                    setSearchVale('');
                  }}
                  noResults={{
                    show: !options?.length,
                    text: <FormattedMessage id={'general.noResults'} />,
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.aerator` })}>
                  {_.chain(filteredAerators)
                    .filter((item) => item.name.toLowerCase().includes(searchValue) && !item.hidden)
                    .sortBy((s) => s.name.toLowerCase())
                    .value()
                    .map((brand) => (
                      <RadioSelectItem
                        value={brand.id}
                        sx={{ width: '100%' }}
                        disabled={brand.isDisabled}
                        key={`${brand.name}_${brand.id}`}>
                        {brand.name}
                      </RadioSelectItem>
                    ))}
                </Select>
                <NumberField
                  dark
                  fullWidth
                  controller={{
                    name: 'quantity',
                    control: aeratorFormControl,
                    rules: {
                      required: intl.formatMessage({ id: 'form.rules.required' }),
                      min: { value: 1, message: intl.formatMessage({ id: 'form.rules.errorMessage' }) },
                      validate: {
                        positive: (value) => (isValidPositive(value) ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                      },
                    },
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.aerators.quantity` })}
                />
              </Box>
              <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={4}>
                <Button type="button" variant="outlined" color="darkBlue" onClick={() => setOpenForm(false)}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.aerators.close`} />
                </Button>
                <Button type="button" color="darkBlue" disabled={!isValid} onClick={handleSubmit(handleAddAerator)}>
                  <FormattedMessage id={'general.add'} values={{ type: '' }} />
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Divider dark />
      <List
        dark
        noResult={{
          show: !rows.length || !rows.some((aerator) => !aerator.isDeleted),
          text: <FormattedMessage id={'general.noResults'} />,
        }}
        options={options}>
        {rows.map((aerator, idx) => {
          const fieldName = `ponds.${fieldIndex}.assignedAerators.${idx}.numberOfAerators`;
          const hasError = !!errors.ponds?.[fieldIndex]?.assignedAerators?.[idx]?.numberOfAerators;

          if (aerator.isDeleted) return null;
          return (
            <ListRow key={aerator.aeratorId}>
              <ListCell>{aerator.aeratorName}</ListCell>
              <ListCell>{aerator.aeratorBrand}</ListCell>
              <ListCell align="center">
                {watchAerators[idx].edit == true ? (
                  <InputNumber
                    dark
                    disableUnderline
                    controller={{
                      control: control,
                      name: fieldName,
                      rules: {
                        required: true,
                        min: 1,
                        validate: {
                          isValid: (value) => isValidPositive(value),
                        },
                      },
                    }}
                    autoFocus={watchAerators[idx].edit}
                    inputProps={{
                      style: {
                        textAlign: 'center',
                      },
                    }}
                    sx={{
                      background: 'white',
                      borderRadius: 1,
                      border: `1px solid ${hasError ? 'red' : theme.palette.darkBlue.main}`,
                      width: 55,
                    }}
                  />
                ) : (
                  watchAerators[idx].numberOfAerators
                )}
              </ListCell>
              <ListCell align="right">
                <FormattedMessage
                  id={`selfService.aerators.types.${AeratorTypes.find((at) => at.id === aerator.aeratorTypeId)?.name}`}
                />
              </ListCell>
              <ListCell align="right">{aerator.horsepower}</ListCell>
              <ListCell align="right">{aerator.supportedBiomass}</ListCell>
              <ListCell align="right">
                <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={2}>
                  {watchAerators[idx].edit == true ? (
                    <>
                      <Mui.IconButton
                        sx={{ background: theme.palette.lightBlue.o75 }}
                        onClick={() => {
                          handleEditRow(idx);
                        }}>
                        <X size={18} />
                      </Mui.IconButton>
                      <Mui.IconButton
                        disabled={hasError}
                        sx={{ background: theme.palette.lightBlue.o75 }}
                        onClick={() => handleConfirm(idx)}>
                        <Check size={18} />
                      </Mui.IconButton>
                    </>
                  ) : (
                    <>
                      <Mui.IconButton sx={{ background: theme.palette.lightBlue.o75 }} onClick={() => handleDeleteAerator(idx)}>
                        <Trash2 size={18} />
                      </Mui.IconButton>
                      <Mui.IconButton
                        sx={{ background: theme.palette.lightBlue.o75 }}
                        onClick={() => {
                          handleEditRow(idx);
                        }}>
                        <Pencil size={18} />
                      </Mui.IconButton>
                    </>
                  )}
                </Box>
              </ListCell>
            </ListRow>
          );
        })}
      </List>
    </Box>
  );
};

export default React.memo(ListAeratorsContainer);
